import { useCallback, useEffect, useMemo, useState } from 'react';
import { getLibrary } from './departmentDocumentsApi';
import { folderContents, paginate, searchLibrary } from './libraryTree';

export const PAGE_SIZE = 50;

// Up to this many documents in the department, a search loads all of them once
// and filters in the browser (instant, no request per keystroke). Above it, every
// search goes to the server. Equal to the server's MAX_PAGE_SIZE, so the
// snapshot always fits in one request.
export const LOCAL_SEARCH_MAX_DOCUMENTS = 200;

const SEARCH_DEBOUNCE_MS = 300;

const EMPTY_PAGE = { documents: [], pages: 0, total: 0 };

/**
 * What the list page shows for one department: a page of a folder when
 * browsing, a page of results when `term` is set. Subfolders (or, when
 * searching, matching folders) come first, on page 1 only.
 */
const useDocumentLibrary = ({ departmentId, folderId, term, page }) => {
  const searching = term.trim().length > 0;

  const [folders, setFolders] = useState([]);
  const [totalDocuments, setTotalDocuments] = useState(null);
  const [serverPage, setServerPage] = useState(EMPTY_PAGE);
  // Every document of the department, for local search. Dropped on any change.
  const [snapshot, setSnapshot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [version, setVersion] = useState(0);

  // A different department starts from nothing known.
  useEffect(() => {
    setFolders([]);
    setTotalDocuments(null);
    setSnapshot(null);
    setServerPage(EMPTY_PAGE);
  }, [departmentId]);

  const reload = useCallback(() => {
    setSnapshot(null);
    setVersion(v => v + 1);
  }, []);

  // A folder id from the URL that is not in this department's tree (a stale
  // link, another department) means the root — the server falls back the same way.
  const openFolderId = folderId && folders.some(f => f._id === folderId) ? folderId : null;

  const localSearch = searching && totalDocuments !== null && totalDocuments <= LOCAL_SEARCH_MAX_DOCUMENTS;
  const snapshotReady = snapshot?.departmentId === departmentId;

  useEffect(() => {
    if (!departmentId) {
      setIsLoading(false);
      return undefined;
    }
    // Local search with the snapshot in hand needs no request at all.
    if (localSearch && snapshotReady) {
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      setError('');
      try {
        let params;
        if (localSearch) params = { department: departmentId, all: 1, limit: LOCAL_SEARCH_MAX_DOCUMENTS };
        else if (searching) params = { department: departmentId, q: term.trim(), page, limit: PAGE_SIZE };
        else params = { department: departmentId, folder: folderId, page, limit: PAGE_SIZE };

        const result = await getLibrary(params);
        if (cancelled) return;
        setFolders(result.folders);
        setTotalDocuments(result.totalDocuments);
        if (localSearch) {
          setSnapshot({ departmentId, documents: result.documents });
        } else {
          setServerPage({ documents: result.documents, pages: result.pages, total: result.total });
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Fetching department documents failed:', err);
        setServerPage(EMPTY_PAGE);
        setError(err?.serverError?.hebMessage || 'טעינת המסמכים נכשלה');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    // Debounce only server searches, which would otherwise fire per keystroke.
    const timer = setTimeout(run, searching && !localSearch ? SEARCH_DEBOUNCE_MS : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `term` matters only to server search; local search filters in the memo below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, folderId, page, searching, localSearch, snapshotReady, localSearch ? '' : term, version]);

  const view = useMemo(() => {
    if (!searching) {
      return {
        folders: page === 1 ? folderContents(folders, [], openFolderId).folders : [],
        ...serverPage,
      };
    }
    const folderHits = page === 1 ? searchLibrary(folders, [], term).folders : [];
    if (localSearch && snapshotReady) {
      const { items, pages, total } = paginate(searchLibrary(folders, snapshot.documents, term).documents, page, PAGE_SIZE);
      return { folders: folderHits, documents: items, pages, total };
    }
    return { folders: folderHits, ...serverPage };
  }, [searching, folders, openFolderId, serverPage, page, term, localSearch, snapshotReady, snapshot]);

  return {
    ...view,
    allFolders: folders,
    openFolderId: searching ? null : openFolderId,
    totalDocuments,
    searchMode: searching ? (localSearch ? 'local' : 'server') : null,
    isLoading,
    error,
    reload,
  };
};

export default useDocumentLibrary;
